import { FoulBuildUpRowData, SubstationBaseConfig } from '../types/tender.types';
import { SUBSTATION_BASE_ROW_DEFS, SUBSTATION_SECTION_ROW_IDS } from './substationBaseRowDefs';

// Column indices (0-based): Description=B(1), Qty=D(3), EvalCode=E(4)
const DESC_COL = 1;
const QUANT_COL = 3;
const EVAL_COL = 4;

// SUB STATION BASE row ssb_3 ("Cart Spoil to onsite bund NE XXXm") — the
// distance value is read straight off this row's own description, same
// row-position approach as compoundExcelUtils.ts's *_PATTERN constants.
const CART_SPOIL_DISTANCE_PATTERN = /Cart\s+Spoil\s+to\s+onsite\s+bund\s+NE\s+(\d+(?:\.\d+)?)\s*m/i;

// SUB STATION BASE row ssb_4 ("XXmm concrete Blinding") — same idea.
const BLINDING_PATTERN = /^(\d+(?:\.\d+)?)\s*mm\s*concrete\s*Blinding/i;

// SUB STATION BASE rows ssb_5/ssb_6/ssb_7 ("Formwork NE X.Xm to ...") — same idea.
const FORMWORK_PERIMETER_PATTERN = /Formwork\s+NE\s+(\d+(?:\.\d+)?)\s*m\s+to\s+external\s+perimeter/i;
const FORMWORK_PIT_PATTERN = /Formwork\s+NE\s+(\d+(?:\.\d+)?)\s*m\s+to\s+form\s+pit\s+to\s+frontage\s+transformer/i;
const FORMWORK_VOID_PATTERN = /Formwork\s+NE\s+(\d+(?:\.\d+)?)\s*m\s+to\s+form\s+walls\s+void\s+area\s+under\s+floor/i;

// SUB STATION BASE row ssb_9 ("Supply & Fix 14 sheets XXXX Mesh") — same idea.
const MESH_PATTERN = /Supply\s*&\s*Fix\s+14\s+sheets\s+(\S+)\s+Mesh/i;

// SUB STATION ACCESS rows ssa_2/ssa_3/ssa_6 keep their own original wording
// ("XXXmm AC32 DBM base course" etc — never Compound's "XXmm thick AC ...mm
// dense bin/surf" phrasing), so they can't be matched by looking for
// Compound's full label text inside the description. Instead, read the
// leading mm number off the row's own description and find the option whose
// label includes that same "XXmm" substring — same includes()-based
// short-form fallback compoundExcelUtils.ts's cp_5 Bindercourse match uses.
const LEADING_MM_PATTERN = /^(\d+(?:\.\d+)?)\s*mm/i;

// ssa_6 has two options sharing the same leading mm (25mm/30mm, split by AC6
// vs AC10), so its match also needs the AC-size number to disambiguate.
const LEADING_MM_AC_PATTERN = /^(\d+(?:\.\d+)?)\s*mm\s*AC\s*(\d+(?:\.\d+)?)/i;

const SSA_BASE_COURSE_LABELS = ['100mm thick AC 32mm dense bin', '150mm thick AC 32mm dense bin'];
const SSA_BINDER_COURSE_LABELS = [
  '50mm thick AC 20mm dense bin',
  '60mm thick AC 20mm dense bin',
  '75mm thick AC 20mm dense bin',
  '80mm thick AC 20mm dense bin',
  '100mm thick AC 20mm dense bin',
];

// SUB STATION ACCESS row ssa_6 ("XXmm AC6 DBM Surface course") — same idea,
// same option list as Compound.tsx's Sales Parking "xxmm Surface Course".
const SSA_SURFACE_COURSE_LABELS = [
  '20mm thick AC 6mm dense surf',
  '25mm thick AC 6mm dense surf',
  '25mm thick AC 10mm dense surf',
  '30mm thick AC 6mm dense surf',
  '30mm thick AC 10mm dense surf',
];

const SECTION_TRIGGERS: Array<{ pattern: RegExp; key: string }> = [
  { pattern: /SUB[\s-]*STATION[\s-]*BASE/i,   key: 'SUB STATION BASE' },
  { pattern: /SUB[\s-]*STATION[\s-]*ACCESS/i, key: 'SUB STATION ACCESS' },
];

const detectSection = (row: unknown[], triggered: Set<string>): string | null => {
  const scan = Math.min(row.length, 8);
  for (let c = 0; c < scan; c++) {
    const val = String(row[c] ?? '').trim();
    if (!val) continue;
    for (const { pattern, key } of SECTION_TRIGGERS) {
      if (!triggered.has(key) && pattern.test(val)) {
        return key;
      }
    }
  }
  return null;
};

// Static (non-configurable) row descriptions, keyed by id — the fallback
// match test below for every row id that has neither a *_PATTERN nor a
// label set of its own.
const SUBSTATION_ROW_DESC: Record<string, string> = Object.fromEntries(
  SUBSTATION_BASE_ROW_DEFS.filter((r) => r.rowType === 'data').map((r) => [r.id, r.description]),
);
const normalizeStatic = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

// Whether a sheet row's own description plausibly *is* the item identified by
// `rowId` — used to find which field a sheet row belongs to by what it says,
// not by its raw position in the sheet (a row the sheet leaves blank or omits
// would otherwise shift every later row in the section onto the wrong id).
// Configurable rows are matched via the same pattern/label set already used
// below to extract their config value; every other row has no known
// variability, so its description is expected to appear in the sheet
// unchanged.
const descMatchesRow = (rowId: string, desc: string): boolean => {
  switch (rowId) {
    case 'ssb_3': return CART_SPOIL_DISTANCE_PATTERN.test(desc);
    case 'ssb_4': return BLINDING_PATTERN.test(desc);
    case 'ssb_5': return FORMWORK_PERIMETER_PATTERN.test(desc);
    case 'ssb_6': return FORMWORK_PIT_PATTERN.test(desc);
    case 'ssb_7': return FORMWORK_VOID_PATTERN.test(desc);
    case 'ssb_9': return MESH_PATTERN.test(desc);
    case 'ssa_2': {
      const mmMatch = desc.match(LEADING_MM_PATTERN);
      return !!mmMatch && SSA_BASE_COURSE_LABELS.some((l) => l.includes(`${mmMatch[1]}mm`));
    }
    case 'ssa_3': {
      const mmMatch = desc.match(LEADING_MM_PATTERN);
      return !!mmMatch && SSA_BINDER_COURSE_LABELS.some((l) => l.includes(`${mmMatch[1]}mm`));
    }
    case 'ssa_6': {
      const mmAcMatch = desc.match(LEADING_MM_AC_PATTERN);
      return !!mmAcMatch && SSA_SURFACE_COURSE_LABELS.some((l) =>
        l.includes(`${mmAcMatch[1]}mm`) && l.includes(`AC ${mmAcMatch[2]}mm`),
      );
    }
    default:
      return normalizeStatic(desc) === normalizeStatic(SUBSTATION_ROW_DESC[rowId] ?? '');
  }
};

const parseSubstationSheet = (
  rows: unknown[][],
): { table: Record<string, FoulBuildUpRowData>; config: SubstationBaseConfig } => {
  const result: Record<string, FoulBuildUpRowData> = {};
  const config: SubstationBaseConfig = {};
  let currentIds: string[] = [];
  let pointer = 0;
  const triggered = new Set<string>();

  for (const row of rows) {
    const sectionKey = detectSection(row, triggered);
    if (sectionKey) {
      triggered.add(sectionKey);
      currentIds = SUBSTATION_SECTION_ROW_IDS[sectionKey] ?? [];
      pointer = 0;
      continue;
    }

    if (!currentIds.length) continue;

    const desc = String(row[DESC_COL] ?? '').trim();
    if (!desc) continue;

    // Find which item this sheet row actually describes — searching forward
    // from the last match rather than trusting position — so a row the
    // sheet leaves blank or omits entirely doesn't shift every later row in
    // the section (including the per-row config extraction keyed off rowId
    // below) onto the wrong field id.
    let matchIndex = -1;
    for (let i = pointer; i < currentIds.length; i++) {
      if (descMatchesRow(currentIds[i], desc)) {
        matchIndex = i;
        break;
      }
    }
    if (matchIndex === -1) continue; // no remaining item in this section describes this row

    const rowId = currentIds[matchIndex];
    pointer = matchIndex + 1;

    if (rowId === 'ssb_3') {
      const match = desc.match(CART_SPOIL_DISTANCE_PATTERN);
      if (match) config.cartSpoilDistanceM = parseFloat(match[1]);
    }
    if (rowId === 'ssb_4') {
      const match = desc.match(BLINDING_PATTERN);
      if (match) config.ssbBlindingMm = parseFloat(match[1]);
    }
    if (rowId === 'ssb_5') {
      const match = desc.match(FORMWORK_PERIMETER_PATTERN);
      if (match) config.ssbFormworkPerimeterM = parseFloat(match[1]);
    }
    if (rowId === 'ssb_6') {
      const match = desc.match(FORMWORK_PIT_PATTERN);
      if (match) config.ssbFormworkPitM = parseFloat(match[1]);
    }
    if (rowId === 'ssb_7') {
      const match = desc.match(FORMWORK_VOID_PATTERN);
      if (match) config.ssbFormworkVoidM = parseFloat(match[1]);
    }
    if (rowId === 'ssb_9') {
      const match = desc.match(MESH_PATTERN);
      if (match) config.ssbMeshType = match[1];
    }
    if (rowId === 'ssa_2') {
      const mmMatch = desc.match(LEADING_MM_PATTERN);
      const label = mmMatch && SSA_BASE_COURSE_LABELS.find((l) => l.includes(`${mmMatch[1]}mm`));
      if (label) config.ssaBaseCourseLabel = label;
    }
    if (rowId === 'ssa_3') {
      const mmMatch = desc.match(LEADING_MM_PATTERN);
      const label = mmMatch && SSA_BINDER_COURSE_LABELS.find((l) => l.includes(`${mmMatch[1]}mm`));
      if (label) config.ssaBinderCourseLabel = label;
    }
    if (rowId === 'ssa_6') {
      const mmAcMatch = desc.match(LEADING_MM_AC_PATTERN);
      const label = mmAcMatch && SSA_SURFACE_COURSE_LABELS.find((l) =>
        l.includes(`${mmAcMatch[1]}mm`) && l.includes(`AC ${mmAcMatch[2]}mm`),
      );
      if (label) config.ssaSurfaceCourseLabel = label;
    }

    // The field slot is already consumed above regardless — this only
    // decides whether a quantity value gets stored for it.
    const rawQuant = row[QUANT_COL];
    if (rawQuant === null || rawQuant === undefined || rawQuant === '') continue;
    const qStr = String(rawQuant).trim();
    if (qStr.startsWith('#')) continue;
    const q = parseFloat(qStr);
    if (isNaN(q)) continue;

    const rawEval = String(row[EVAL_COL] ?? '').trim();
    const evaluateCode = rawEval.startsWith('#') ? '' : rawEval;

    result[rowId] = { quant: q, evaluateCode };
  }

  return { table: result, config };
};

export const parseSubstationBaseFromExcel = async (
  file: File,
): Promise<{ table: Record<string, FoulBuildUpRowData>; config: SubstationBaseConfig }> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: true });

  const sheetName =
    workbook.SheetNames.find((n) => /^substation[\s-]*base$/i.test(n.trim())) ??
    workbook.SheetNames.find((n) => /substation/i.test(n));

  if (!sheetName) {
    throw new Error(
      `Sheet "Substation Base" not found. Available sheets: ${workbook.SheetNames.join(', ')}`,
    );
  }

  const ws = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, {
    header: 1, raw: true, defval: '',
  }) as unknown[][];

  return parseSubstationSheet(rows);
};
